import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { X } from 'lucide-react';
import { formatCents, type InventoryStatus, type ProductStatus } from '@hutdrop/shared';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'secondary',
  busy,
  children,
  className,
  size,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean; size?: 'sm' }) {
  return (
    <button
      type="button"
      className={['btn', `btn--${variant}`, size === 'sm' ? 'btn--sm' : '', className ?? ''].join(' ').trim()}
      disabled={busy || rest.disabled}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="spinner spinner--sm" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

interface FieldShell {
  label: string;
  id: string;
  error?: string | undefined;
  hint?: ReactNode;
  className?: string;
}

function Shell({ label, id, error, hint, className, children }: FieldShell & { children: ReactNode }) {
  return (
    <div className={`fld${className ? ` ${className}` : ''}`}>
      <label htmlFor={id} className="fld__label">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${id}-err`} className="fld__error">
          {error}
        </p>
      ) : hint ? (
        <p className="fld__hint">{hint}</p>
      ) : null}
    </div>
  );
}

export function TextField({ label, id, error, hint, className, ...input }: FieldShell & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <Shell label={label} id={id} error={error} hint={hint} className={className}>
      <input id={id} className="fld__input" aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-err` : undefined} {...input} />
    </Shell>
  );
}

export function TextArea({ label, id, error, hint, className, ...input }: FieldShell & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <Shell label={label} id={id} error={error} hint={hint} className={className}>
      <textarea id={id} className="fld__input fld__textarea" aria-invalid={error ? true : undefined} {...input} />
    </Shell>
  );
}

export function SelectField({
  label,
  id,
  error,
  hint,
  className,
  children,
  ...select
}: FieldShell & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <Shell label={label} id={id} error={error} hint={hint} className={className}>
      <select id={id} className="fld__input" aria-invalid={error ? true : undefined} {...select}>
        {children}
      </select>
    </Shell>
  );
}

export function Toggle({ label, checked, onChange, disabled, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hint?: string }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle__track" aria-hidden="true" />
      <span className="toggle__text">
        {label}
        {hint ? <span className="muted small toggle__hint">{hint}</span> : null}
      </span>
    </label>
  );
}

const STOCK_LABEL: Record<InventoryStatus, string> = { IN_STOCK: 'In stock', LOW_STOCK: 'Low stock', OUT_OF_STOCK: 'Out of stock' };
const STATUS_LABEL: Record<ProductStatus, string> = { ACTIVE: 'Active', INACTIVE: 'Inactive', ARCHIVED: 'Archived' };

export function StockBadge({ status }: { status: InventoryStatus }) {
  return <span className={`badge badge--${status.toLowerCase().replaceAll('_', '-')}`}>{STOCK_LABEL[status]}</span>;
}

export function StatusBadge({ status }: { status: ProductStatus }) {
  return <span className={`badge badge--status-${status.toLowerCase()}`}>{STATUS_LABEL[status]}</span>;
}

export function Price({ cents, saleCents }: { cents: number; saleCents?: number | null }) {
  if (saleCents == null) return <span className="sd-num">{formatCents(cents)}</span>;
  return (
    <span className="price">
      <span className="price__sale sd-num">{formatCents(saleCents)}</span>
      <s className="price__was sd-num">{formatCents(cents)}</s>
    </span>
  );
}

export function Alert({ tone = 'danger', children }: { tone?: 'danger' | 'success' | 'info'; children: ReactNode }) {
  return (
    <div className={`alert alert--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Empty({ icon, title, children }: { icon: ReactNode; title: string; children?: ReactNode }) {
  return (
    <section className="empty">
      <span aria-hidden="true">{icon}</span>
      <h2>{title}</h2>
      {children ? <div className="muted">{children}</div> : null}
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page__header page__header--row">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p className="muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page__actions">{actions}</div> : null}
    </header>
  );
}

/** Native <dialog>: focus trapping, Esc to close and inert background come from the browser. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className={`dialog${wide ? ' dialog--wide' : ''}`} onClose={onClose} aria-labelledby="dialog-title">
      {open ? (
        <>
          <header className="dialog__head">
            <h2 id="dialog-title">{title}</h2>
            <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
              <X size={18} />
            </button>
          </header>
          <div className="dialog__body">{children}</div>
          {footer ? <footer className="dialog__foot">{footer}</footer> : null}
        </>
      ) : null}
    </dialog>
  );
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Dialog>
  );
}

export function Skeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="table-skeleton" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton-row" />
      ))}
    </div>
  );
}
